let USE_RTA = false;
USE_RTA = true;

import ENV from "app/utils/ENV";
import React from "react";
import {
  View,
  ScrollView,
  ActivityIndicator,
  Keyboard,
  Dimensions,
} from "react-native";
import TouchableOpacity from "app/components/TouchableOpacity";
import Text from "app/components/Text";
import TextInput from "app/components/TextInput";
import DatePicker from "app/components/DatePicker";
import momentActions from "app/utils/momentActions";
import valueActions from "app/utils/valueActions";
import textHelpers from "app/utils/textHelpers";
import richTextHelpers from "app/utils/richTextHelpers";
import colorHelpers from "app/utils/colorHelpers";
import timeHelpers from "app/utils/timeHelpers";
import chapterHelpers from "app/utils/chapterHelpers";
import chapterActions from "app/utils/chapterActions";
import * as RootNavigation from "app/utils/RootNavigation.js";
import { Entypo, Feather, MaterialIcons } from "@expo/vector-icons";

import RichTextArea from "app/components/RichTextArea";
import HoverableTooltip from "app/components/HoverableTooltip";
import Badge from "app/components/chapter/Badge";
import Autocomplete from "app/components/Autocomplete";

import store from "app/redux/store";
import { add as addValuesToStore } from "app/actions/values";
import { StyleContext } from "app/contexts/Style";

export default class MomentNote extends React.Component {
  static contextType = StyleContext;
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      text: props.moment.text,
      title: props.moment.title,
      bodyQuery: "",
      bodySelection: null,
      injectedRefs: [],
      titleQuery: "",
      titleQuerySuggestion: null,
      expanded: props.expanded || false,
      query: "",
      selection: null,
      showDatePicker: false,
      caretPos: 0,
      keyboardHeight: 0,
    };
  }

  componentDidMount = () => {
    if (ENV.WEB) return;
    // track the virtual keyboard so the mobile body editor can fill the
    // space above it (keyboardWillShow is iOS-only; Android fires did*)
    let onShow = (e) =>
      this.setState({ keyboardHeight: e?.endCoordinates?.height || 0 });
    let onHide = () => this.setState({ keyboardHeight: 0 });
    this.keyboardSubs = [
      Keyboard.addListener("keyboardWillShow", onShow),
      Keyboard.addListener("keyboardDidShow", onShow),
      Keyboard.addListener("keyboardDidHide", onHide),
    ];
  };

  componentWillUnmount = () => {
    this.keyboardSubs?.forEach((s) => s?.remove && s.remove());
  };

  // On mobile the editor should be as tall as possible without hiding
  // behind the virtual keyboard. Before the keyboard event lands, assume a
  // typical keyboard footprint so the editor never opens tiny.
  mobileEditorHeight = () => {
    let windowHeight = Dimensions.get("window").height;
    let keyboardHeight = this.state.keyboardHeight || windowHeight * 0.4;
    // allowance for the moment card header, date row, and surrounding chrome
    let chrome = 160;
    return Math.max(200, windowHeight - keyboardHeight - chrome);
  };

  componentDidUpdate = (prevProps) => {
    // adopt refreshed moment data unless the user is mid-edit
    let { moment } = this.props;
    let prev = prevProps.moment;
    if (
      (prev.text !== moment.text || prev.title !== moment.title) &&
      !this.state.editing &&
      !this.state.editingTitle
    ) {
      this.setState({ text: moment.text, title: moment.title });
    }
  };

  focus = () => {
    if (!this.state.editing) {
      this.setState({ editing: true });
    } else {
      this.inputRef?.focus();
    }
  };

  render() {
    let { moment, value, rootMoment, chapter, color, focused, isPinned } =
      this.props;
    let {
      text,
      title,
      titleQuery,
      expanded,
      editing,
      editingTitle,
      showDatePicker,
      caretPos,
      bodyQuery,
    } = this.state;
    let isComplete = !!moment.completed_at;
    let isPin = !!chapter && chapter.range > 1;
    let isProgress = isComplete && !text?.length;
    let isEmptyPin = !isComplete && !text?.length && true;
    // only today's card invites input: it alone shows a placeholder and
    // stays wide enough to fit it — past empty cards stay narrow and mute
    let isToday =
      !!chapter && chapter.range == 1 && timeHelpers.wasToday(chapter.start_at);
    let isValueCompletion = moment?.type == "completion";
    let isValueArchive = moment?.type == "archive";
    // !!chapter &&
    // chapter.range == 1 &&
    // !timeHelpers.wasToday(chapter.start_at);

    let chapterTitle = isValueCompletion
      ? `Completed on ${textHelpers.formatDate(
          moment.completed_at,
          true,
          false,
        )}`
      : isValueArchive
      ? `Archived on ${textHelpers.formatDate(
          moment.completed_at,
          true,
          false,
        )}`
      : !!chapter
      ? chapterHelpers.getTitle(chapter, false, true)
      : "No chapter?"; // textHelpers.formatDate(moment.created_at, true);

    // vertical pins spell out their timeframe: "Week going into Sep",
    // "Month of September", "Year of 2026"
    if (isPin && !!chapter?.range && chapter.range > 1) {
      let base = chapterHelpers.getTitle(chapter, false, false) || "";
      if (chapter.range == 7) {
        chapterTitle = /^into /i.test(base)
          ? `Week going ${base.charAt(0).toLowerCase()}${base.slice(1)}`
          : `Week of ${base}`;
      } else if (chapter.range >= 28 && chapter.range <= 31) {
        chapterTitle = `Month of ${base}`;
      } else if (chapter.range >= 365) {
        chapterTitle = `Year of ${base}`;
      }
    }

    let paleColor = colorHelpers.hexToRgbaString(color, 0.2);

    // let renderVertically = isPin || (isProgress && !editing);
    let renderVertically =
      (isPin || isValueCompletion || isValueArchive) &&
      !editing &&
      !editingTitle &&
      !text?.length;

    return (
      <TouchableOpacity
        activeOpacity={1}
        onPress={
          renderVertically
            ? isValueCompletion
              ? this.handleToggleValueCompletion
              : isValueArchive
              ? undefined
              : () => this.setState({ editing: true })
            : undefined
        }
        style={[
          this.styles().wrapper,
          {
            width: this.props.width,
            maxWidth: "100%",
          },
          (expanded || editing || showDatePicker || editingTitle) &&
            this.styles().expandedWrapper,
          (isProgress || isEmptyPin) &&
            // !focused &&
            !editing &&
            !isToday &&
            this.styles().progressWrapper,
          isEmptyPin && !editing && isToday && this.styles().todayEmptyWrapper,
          (isComplete || isPin) && this.styles().completedWrapper,
          focused && { borderWidth: 1, borderColor: color },
          // isComplete || isPin ? { backgroundColor: paleColor } : {}, //{ borderColor: color },
          isComplete ? { backgroundColor: paleColor } : {}, //{ borderColor: color },
          renderVertically && this.styles().verticalWrapper,
          renderVertically && ENV.WEB && { cursor: "pointer" },
          // isPin && { backgroundColor: color },
        ]}
      >
        <View style={this.styles().headerRow}>
          {renderVertically && !ENV.WEB ? (
            // native can't reliably rotate a bare Text — rotate a wrapping
            // View instead
            <View style={this.styles().verticalTextWrapper}>
              <Text
                medium
                style={[{ color }, this.styles().titleText]}
                onLongPress={() => this.setState({ showDatePicker: true })}
                numberOfLines={1}
              >
                {chapterTitle}
              </Text>
            </View>
          ) : (
          <Text
            medium
            // style={[isComplete && { color }]}
            style={[
              { color },
              this.styles().titleText,
              renderVertically && this.styles().verticalText,
            ]}
            onPress={!renderVertically ? this.goToChapter : undefined}
            onLongPress={() => this.setState({ showDatePicker: true })}
            // numberOfLines={1}
            ellipsizeMode="tail"
          >
            {chapterTitle}
          </Text>
          )}
          {!renderVertically && (!!title?.length || editingTitle) && (
            <Text style={{ color: isComplete ? color : paleColor }}> – </Text>
          )}
          {(!!title?.length || editingTitle) && (
          <TextInput
            style={{
              // marginLeft: this.context.sizes.base,
              color: isComplete ? color : paleColor,
            }}
            numberOfLines={1}
            placeholder={"Title"}
            placeholderTextColor={paleColor}
            onChangeText={(title) => {
              let titleQuery = title.split("#")[1] || "";
              this.setState({ title, titleQuery });
              if (!titleQuery?.lenght) {
                this.handleChangeText();
              }
            }}
            onBlur={() => {
              this.stopEditing();
              this.setState({ editingTitle: false });
            }}
            onFocus={() => this.setState({ editingTitle: true })}
            onEnter={() => {
              this.toggleParent(this.state.titleQuerySuggestion);
            }}
          >
            {title}
          </TextInput>
          )}
          <View style={[this.styles().autocompleteWrapper]}>
            <Autocomplete
              ref={(r) => (this.autocompleteRef = r)}
              suggestionsOnly
              simpleListView
              query={titleQuery}
              // excludeIds={!!value?.id ? [value.id, ...value.parent_ids] : []}
              backgroundColor={this.context.colors.trim}
              onSelectItem={(value, e) => {
                e.preventDefault();
                this.toggleParent(value);
              }}
              setFirstSuggestion={(titleQuerySuggestion) =>
                this.setState({ titleQuerySuggestion })
              }
            />
          </View>
          <View style={{ flexDirection: "row" }}>
            {editing && (
              <HoverableTooltip delay text="Done editing">
                <Feather
                  style={{
                    fontSize: this.context.sizes.large,
                    color: isComplete ? paleColor : this.context.colors.trim,
                  }}
                  name="x"
                  onPress={() => this.setState({ editing: false })}
                />
              </HoverableTooltip>
            )}
            {editing && !isPin && (
              <HoverableTooltip delay text="Convert into a child value">
                <Feather
                  style={{
                    fontSize: this.context.sizes.large,
                    color: isComplete ? paleColor : this.context.colors.trim,
                  }}
                  name="arrow-up-circle"
                  onPress={this.handleConvertToValue}
                />
              </HoverableTooltip>
            )}
            {showDatePicker && (
              <View style={{ flexDirection: "row" }}>
                <DatePicker
                  dateOnly
                  selected={
                    chapter?.start_at
                      ? new Date(chapter.start_at)
                      : new Date(moment.created_at)
                  }
                  value={
                    chapter?.start_at
                      ? new Date(chapter.start_at)
                      : new Date(moment.created_at)
                  }
                  onChange={(date) => this.handleChangeDate(date)}
                />
                <Feather
                  name="x"
                  style={{
                    fontSize: this.context.sizes.large,
                    color: isComplete ? paleColor : this.context.colors.trim,
                  }}
                  onPress={() => this.setState({ showDatePicker: false })}
                />
              </View>
            )}
            {!showDatePicker && !isPin && (editing || isPinned) && (
              <HoverableTooltip
                delay
                text={isPinned ? "Unpin from the top of this log" : "Pin to the top of this log"}
              >
                <MaterialIcons
                  name={"push-pin"}
                  style={{
                    fontSize: this.context.sizes.large,
                    color: isPinned
                      ? color
                      : isComplete
                      ? paleColor
                      : this.context.colors.trim,
                  }}
                  onPress={this.togglePinned}
                />
              </HoverableTooltip>
            )}
            {!showDatePicker && (
              <HoverableTooltip
                delay
                text={isPin ? "Unpin" : isComplete ? "Re-open (⌘↩)" : "Complete (⌘↩)"}
              >
              <Badge
                onPress={() => this.handleMetaEnter(isPin)}
                onLongPress={() => this.setState({ showDatePicker: true })}
                backgroundColor={(isComplete || isPin) && color}
                // ringColor={isPin && color}
                iconColor={
                  isComplete || isPin
                    ? this.context.colors.lightForeground
                    : isPin
                    ? color
                    : undefined
                }
                // backgroundColor={isComplete ? color : this.context.colors.trim}
                // iconColor={
                //   isComplete
                //     ? this.context.colors.lightForeground
                //     : this.context.colors.empty
                // }
                checkIcon
                pinIcon={isPin}
                noTooltip
                ignoreHover
                placeholder={!isComplete && !isPin}
                // style={{ marginLeft: this.context.sizes.small }}
              />
              </HoverableTooltip>
            )}
          </View>
        </View>

        {/* deeper-generation moments: show which value this belongs to */}
        {this.props.showValueTitle && !!value && (
          <Text
            bold
            numberOfLines={1}
            style={{ color, marginBottom: this.context.sizes.tiny }}
          >
            {value.title || value.text}
          </Text>
        )}

        {USE_RTA && !editing ? (
          <ScrollView
            style={[this.styles().scrollViewWrapper]}
            // contentContainerStyle={{}}
          >
            <RichTextArea
              text={text || ""}
              multiline
              placeholder={isToday && !isComplete && !isPin ? "What happened?" : ""}
              references={[
                ...(value?.references || []),
                ...this.state.injectedRefs,
              ]}
              // wrapperStyle={this.styles().input}
              onFocus={() => {
                if (this.stopEditingTimeout)
                  clearTimeout(this.stopEditingTimeout);
                this.setState({
                  editing: true,
                  beganEditingAt: new Date().getTime(),
                });
                // !!this.props.onFocus && this.props.onFocus();
              }}
              onChangeText={(text) => {
                this.setState({ text, isDirty: true });
                this.handleChangeText();
                this.applySelection();
              }}
              onStopEditing={() => {
                this.stopEditing();
                this.applySelection();
              }}
              // numberOfLines={editing ? undefined : isProgress ? 1 : 6}
              onBlur={this.stopEditing}
              // onKeyPress={async (event) => {
              //   let key = event.nativeEvent.key;
              //   if (key == "Escape") {
              //     this.stopEditing();
              //   }
              //   // this.applyQuery();
              // }}
              onEscape={() => {
                this.stopEditing();
                this.setState({ editing: false });
                // hosting bullet (e.g. tarot item) may dismiss on escape
                this.props.onBrowseEscape && this.props.onBrowseEscape();
              }}
              onMetaEnter={() => this.handleMetaEnter(isPin)}
              onMetaShiftEnter={this.completeMomentAndValue}
              onMetaBackspace={this.handleUnpinMoment}
            />
          </ScrollView>
        ) : (
          <TextInput
            multiline
            autoFocus
            inputRef={(r) => (this.inputRef = r)}
            // autoHeight measures DOM scrollHeight, so it's web-only; on
            // native it bottoms out at ~2 lines — size the editor explicitly
            // to fill the space above the virtual keyboard instead
            autoHeight={ENV.WEB ? editing : false}
            placeholder={isToday && !isComplete && !isPin ? "What happened?" : ""}
            // placeholderTextColor={isComplete && color}
            style={[
              this.styles().input,
              !ENV.WEB &&
                editing && {
                  height: this.mobileEditorHeight(),
                  maxHeight: this.mobileEditorHeight(),
                },
            ]}
            onSelectionChange={(e) =>
              this.handleBodySelection(e?.nativeEvent?.selection)
            }
            onFocus={() => {
              if (this.stopEditingTimeout) clearTimeout(this.stopEditingTimeout);
              this.setState({ editing: true });
              // !!this.props.onFocus && this.props.onFocus();
            }}
            onChangeText={(text) => {
              this.setState({ text, isDirty: true });
              this.handleChangeText();
              this.applySelection();
            }}
            onStopEditing={() => {
              this.stopEditing();
              this.applySelection();
            }}
            // Android caps a multiline input's height by numberOfLines —
            // don't fight the explicit editor height while editing on mobile
            numberOfLines={isProgress ? 1 : ENV.WEB || !editing ? 6 : undefined}
            // onMetaUp={(e) => {
            //   this.props.onMetaUp && this.props.onMetaUp(e);
            //   expandable && expanded && this.setState({ expanded: false });
            // }}
            // onMetaDown={(e) => {
            //   this.props.onMetaDown && this.props.onMetaDown(e);
            //   expandable && !expanded && this.setState({ expanded: true });
            // }}
            // metaUpOnlyFromTop={this.props.metaUpOnlyFromTop}
            // onMetaCtrlRight={this.props.onMetaCtrlRight}
            // onMetaCtrlShiftRight={this.props.onMetaCtrlShiftRight}
            onMetaEnter={this.toggleComplete}
            onMetaShiftEnter={this.completeMomentAndValue}
            onMetaBackspace={this.handleUnpinMoment}
            onTab={() => this.indentBodyLine()}
            onShiftTab={() => this.indentBodyLine(true)}
            onMetaK={this.toggleStrikeAtCaret}
            onEnter={ENV.WEB ? this.handleBodyEnter : undefined}
            onEscape={() => {
              this.stopEditing();
              this.setState({
                editing: false,
                bodyQuery: "",
                bodySelection: null,
              });
              // hosting bullet (e.g. tarot item) may dismiss on escape
              this.props.onBrowseEscape && this.props.onBrowseEscape();
            }}
            onBlur={this.stopEditing}
            // style={{
            //   lineHeight: this.context.sizes.default * 1.6,
            //   minHeight: this.context.sizes.giant,
            //   borderWidth: 10,
            // }}
            // onMouseUp={() => {
            //   this.applyQuery();
            // }}
            // onKeyPress={async (event) => {
            //   let key = event.nativeEvent.key;
            //   console.log(key, caretPos);
            //   if (key == "Escape") {
            //     this.stopEditing();
            //   } else if (key == "ArrowUp") {
            //     if (caretPos == 0) {
            //       console.log(this.props.focusPrevious);
            //       this.props.focusPrevious && this.props.focusPrevious();
            //     }
            //   } else if (key == "ArrowDown") {
            //     if (caretPos == text.length) {
            //       console.log(this.props.focusNext);
            //       this.props.focusNext && this.props.focusNext();
            //     }
            //   }
            // }}
            onSetCaret={(caretPos) => this.setState({ caretPos })}
            focusPrevious={this.props.focusPrevious}
            focusNext={this.props.focusNext}
            onAltUp={(e) => {
              this.props.focusPrevious && this.props.focusPrevious();
            }}
            onAltDown={(e) => {
              this.props.focusNext && this.props.focusNext();
            }}
            onAltLeft={(e) => {
              // Ignore if cmd or shift are also pressed (allow default behavior)
              if (e?.metaKey || e?.ctrlKey || e?.shiftKey) return false;
              // Only jump if cursor is at the beginning of the text
              if (this.state.caretPos === 0) {
                this.props.focusPreviousMoment && this.props.focusPreviousMoment();
                return true; // Prevent default
              }
              return false; // Allow default word-jump behavior
            }}
            onAltRight={(e) => {
              // Ignore if cmd or shift are also pressed (allow default behavior)
              if (e?.metaKey || e?.ctrlKey || e?.shiftKey) return false;
              // Only jump if cursor is at the end of the text
              let text = this.state.text || "";
              if (this.state.caretPos === text.length) {
                this.props.focusNextMoment && this.props.focusNextMoment();
                return true; // Prevent default
              }
              return false; // Allow default word-jump behavior
            }}
            onMetaLeft={(e) => {
              // Only jump if cursor is at the beginning of the text
              if (this.state.caretPos === 0) {
                this.props.focusPreviousMoment && this.props.focusPreviousMoment();
                return true; // Prevent default
              }
              return false; // Allow default jump-to-start behavior
            }}
            onMetaRight={(e) => {
              // Only jump if cursor is at the end of the text
              let text = this.state.text || "";
              if (this.state.caretPos === text.length) {
                this.props.focusNextMoment && this.props.focusNextMoment();
                return true; // Prevent default
              }
              return false; // Allow default jump-to-end behavior
            }}
          >
            {text}
          </TextInput>
        )}
        {!!bodyQuery?.length && (
          <View style={[this.styles().autocompleteWrapper]}>
            <Autocomplete
              suggestionsOnly
              simpleListView
              query={bodyQuery}
              excludeIds={!!value?.id ? [value.id] : []}
              backgroundColor={this.context.colors.trim}
              onSelectItem={(v, e) => {
                e.preventDefault();
                this.injectReference(v);
              }}
              onEnter={(v, e) => {
                e.preventDefault();
                if (!!v) this.injectReference(v);
              }}
              submitFirstOnPressEnter
              submitFirstOnPressTab
            />
          </View>
        )}
      </TouchableOpacity>
    );
  }

  toggleParent = async (parent) => {
    if (!parent?.id) {
      alert("no suggestion present");
      return;
    }
    let { moment } = this.props;
    let value_ids = [...moment.value_ids];
    if (value_ids.includes(parent.id)) {
      value_ids = value_ids.filter((x) => x != parent.id);
    } else {
      value_ids.push(parent.id);
    }
    let res = await momentActions.update(moment.id, { value_ids });
    let title = this.state.title.split("#")[0];
    this.setState({ titleQuery: "", titleQuerySuggestion: null, title });
  };

  stopEditing = () => {
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    this.handleChangeText(0);
    // blur returns the card to read mode so links/styling render — the
    // short delay keeps a click landing on the suggestion overlay alive
    if (this.stopEditingTimeout) clearTimeout(this.stopEditingTimeout);
    this.stopEditingTimeout = setTimeout(() => {
      this.setState({ editing: false });
    }, 250);
    // if no suggestion gets picked, tidy the lingering overlay
    if (this.overlayCleanupTimeout) clearTimeout(this.overlayCleanupTimeout);
    this.overlayCleanupTimeout = setTimeout(() => {
      this.setState({ bodyQuery: "", bodySelection: null });
    }, 900);
  };

  handleChangeText = async (delay = 1000) => {
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(this.updateMoment, delay);
  };

  // selection in the plain editing textarea → suggestion query, same flow
  // as the scratchpad's line editor but over the whole moment text
  // Tab / shift+Tab in the body editor indents the current line with a
  // leading tab, mirroring the value scratchpad's line indent behavior
  indentBodyLine = (unindent = false) => {
    let text = this.state.text || "";
    let caret = this.inputRef?.selectionStart;
    if (caret == null) caret = text.length;
    let lineStart = text.lastIndexOf("\n", Math.max(0, caret - 1)) + 1;
    let newText;
    let delta;
    if (unindent) {
      if (text[lineStart] === "\t") {
        newText = text.slice(0, lineStart) + text.slice(lineStart + 1);
        delta = -1;
      } else if (text.slice(lineStart, lineStart + 2) === "  ") {
        newText = text.slice(0, lineStart) + text.slice(lineStart + 2);
        delta = -2;
      } else return;
    } else {
      newText = text.slice(0, lineStart) + "\t" + text.slice(lineStart);
      delta = 1;
    }
    this.setState({ text: newText, isDirty: true });
    this.handleChangeText();
    // restore the caret next to where it was (Tab moves it with the line)
    let newPos = Math.max(lineStart, caret + delta);
    setTimeout(() => {
      try {
        if (this.inputRef?.setSelectionRange)
          this.inputRef.setSelectionRange(newPos, newPos);
        else if (this.inputRef?.setNativeProps)
          this.inputRef.setNativeProps({
            selection: { start: newPos, end: newPos },
          });
      } catch (e) {}
    }, 0);
  };

  // Enter in the body editor continues bullet ("- ") and numbered ("1. ")
  // lists like the scratchpad does; Enter on an empty list item ends the
  // list. Web-only: the wrapper's onEnter preventDefaults the native
  // newline, so we insert it (plus the marker) ourselves.
  handleBodyEnter = () => {
    let text = this.state.text || "";
    let caret = this.inputRef?.selectionStart;
    let selEnd = this.inputRef?.selectionEnd;
    if (caret == null) caret = text.length;
    if (selEnd == null) selEnd = caret;

    let lineStart = text.lastIndexOf("\n", Math.max(0, caret - 1)) + 1;
    let lineEnd = text.indexOf("\n", lineStart);
    if (lineEnd < 0) lineEnd = text.length;
    let line = text.slice(lineStart, lineEnd);

    let bulletMatch = line.match(richTextHelpers.bulletRegex);
    let numberedMatch = line.match(richTextHelpers.numberedRegex);
    let indentation = line.match(/^[\t ]*/)[0];
    let marker = (bulletMatch || numberedMatch || [""])[0];
    let bodyAfterMarker = line.slice(marker.length).trim();

    let newText;
    let newPos;
    if (!!marker && !bodyAfterMarker.length) {
      // empty list item — strip the marker and stop the list
      newText = text.slice(0, lineStart) + indentation + text.slice(lineEnd);
      newPos = lineStart + indentation.length;
    } else {
      let prefix = "";
      if (!!bulletMatch) prefix = "- ";
      else if (!!numberedMatch)
        prefix = `${parseInt(numberedMatch[2]) + 1}${numberedMatch[3]} `;
      let insert = `\n${indentation}${prefix}`;
      newText = text.slice(0, caret) + insert + text.slice(selEnd);
      newPos = caret + insert.length;
    }

    this.setState({ text: newText, isDirty: true });
    this.handleChangeText();
    setTimeout(() => {
      try {
        if (this.inputRef?.setSelectionRange)
          this.inputRef.setSelectionRange(newPos, newPos);
      } catch (e) {}
    }, 0);
  };

  // ⌘K: toggle ~strike~ on the line(s) under the caret/selection, mirroring
  // the scratchpad's strikeLines semantics (wrap after the indent/bullet
  // prefix; unstrike removes all tildes from the line)
  toggleStrikeAtCaret = () => {
    let text = this.state.text || "";
    let selStart = this.inputRef?.selectionStart;
    let selEnd = this.inputRef?.selectionEnd;
    if (selStart == null) selStart = text.length;
    if (selEnd == null) selEnd = selStart;

    let lines = text.split(/[\r\n]/);
    let targets = [];
    let charCount = 0;
    lines.forEach((l, j) => {
      let start = charCount;
      let end = charCount + l.length;
      if (end >= selStart && start <= selEnd) targets.push(j);
      charCount = end + 1;
    });
    if (!targets.length) return;

    let shouldStrike = !lines[targets[0]]?.includes("~");
    let newText = lines
      .map((l, j) => {
        if (!targets.includes(j)) return l;
        else if (!shouldStrike) return l.replace(/\~/g, "");
        else if (l.includes("~")) return l;
        let startingIndex = richTextHelpers.findStartingIndex(l);
        return `${l.slice(0, startingIndex)}~${l.slice(startingIndex)}~`;
      })
      .join("\n");
    if (newText == text) return;

    this.setState({ text: newText, isDirty: true });
    this.handleChangeText();
    // keep the caret near where it was (a leading ~ shifts it by one)
    if (selStart == selEnd) {
      let newPos = Math.max(0, selStart + (shouldStrike ? 1 : -1));
      setTimeout(() => {
        try {
          if (this.inputRef?.setSelectionRange)
            this.inputRef.setSelectionRange(newPos, newPos);
        } catch (e) {}
      }, 0);
    }
  };

  handleBodySelection = (selection) => {
    if (this.selectionQueryTimeout) clearTimeout(this.selectionQueryTimeout);
    // let the drag settle so mid-selection states don't thrash the list
    this.selectionQueryTimeout = setTimeout(() => {
      let { start, end } = selection || {};
      let { text } = this.state;
      if (start != null && end != null && end > start) {
        let query = (text || "").slice(start, end).trim();
        if (!!query.length && query.length < 80) {
          this.setState({ bodyQuery: query, bodySelection: [start, end] });
          return;
        }
      }
      this.setState({ bodyQuery: "", bodySelection: null });
    }, 250);
  };

  // same link markup the scratchpad inserts: the selected text is replaced
  // by {valueId}, which read-mode RichText renders as the value chip
  injectReference = async (refVal) => {
    let { text, bodySelection, injectedRefs } = this.state;
    if (!refVal?.id || !bodySelection) return;
    if (this.overlayCleanupTimeout) clearTimeout(this.overlayCleanupTimeout);
    // make sure the picked value survives remounts: autocomplete results
    // aren't necessarily in the redux store, and RichText's fallback reads it
    store.dispatch(addValuesToStore([refVal]));
    let newText = `${(text || "").slice(0, bodySelection[0])}{${refVal.id}}${(
      text || ""
    ).slice(bodySelection[1])}`;
    await this.setState({
      text: newText,
      isDirty: true,
      bodyQuery: "",
      bodySelection: null,
      // keep the picked value renderable immediately — the server only
      // folds moment-text refs into value.references on later fetches
      injectedRefs: [...injectedRefs, refVal].filter(
        (v, i, a) => a.findIndex((x) => x.id == v.id) == i,
      ),
    });
    this.handleChangeText();
  };

  applySelection = async (selection = null) => {
    await new Promise((r) => setTimeout(r, 0));
    let { text } = this.state;
    text = text || "";
    let query = "";
    if (!!selection) {
      let line = text.split(/[\r\n]/)[selection[2]];
      query = line?.slice(selection[0], selection[1]) || "";
    } else {
      query = text.match(/#[^ \n\r#].*/);
      query = !!query ? query[0] : "#";
      query = query.slice(1);
    }
    // }
    // console.log(query, selection);
    this.setState({ query, selection });
  };

  applyQuery = async () => {
    return;
  };

  updateMoment = async () => {
    let { moment } = this.props;
    let { text, title } = this.state;
    let res = await momentActions.update(moment.id, { title, text });
    // console.warn("updated moment", res);
    if (!res?.success) {
      console.warn("MomentNote update failed");
    } else {
      this.props.onUpdateText && this.props.onUpdateText(text);
    }
  };

  goToChapter = (e) => {
    let { moment, chapter } = this.props;
    let date =
      chapter?.start_at || timeHelpers.dateTimeToDate(moment.created_at);
    let range = !!chapter ? chapterHelpers.getChapterChunk(chapter) : "week";
    RootNavigation.push("Chapters", { date, range }, e);
  };

  handleChangeDate = async (date) => {
    let { moment } = this.props;
    let dateStr = timeHelpers.dateTimeToDate(date);

    // Check if the date actually changed
    let currentDate =
      this.props.chapter?.start_at ||
      timeHelpers.dateTimeToDate(moment.created_at);
    if (dateStr === currentDate) {
      this.setState({ showDatePicker: false });
      return;
    }

    // Find an existing day chapter for the target date in the Redux store
    let chapters = store.getState().chapters;
    let targetChapter = chapters.find(
      (c) => c.range === 1 && c.start_at === dateStr,
    );

    let chapterId;
    if (targetChapter) {
      chapterId = targetChapter.id;
    } else {
      // Create a new day chapter for this date
      let res = await chapterActions.create({
        start_at: dateStr,
        end_at: dateStr,
        range: 1,
      });
      if (!res?.success || !res?.chapter?.id) {
        alert("Could not find or create a chapter for that date");
        return;
      }
      chapterId = res.chapter.id;
    }

    // Move the moment to the new chapter
    let res = await momentActions.update(moment.id, { chapter_id: chapterId });
    if (res?.success) {
      this.setState({ showDatePicker: false });
      this.props.onRefreshChapter && this.props.onRefreshChapter();
      this.props.onRefreshMoment && this.props.onRefreshMoment();
    } else {
      alert("Failed to move moment to the selected date");
    }
  };

  handleMetaEnter = (isPin) => {
    let { moment } = this.props;
    if (moment?.type == "completion") {
      // the value-completion pseudo card has no moment behind it —
      // its check re-opens the value itself
      this.handleToggleValueCompletion();
      return;
    }
    // confirm?
    if (isPin) {
      alert("unpin?");
    } else {
      this.toggleComplete();
    }
  };

  handleToggleValueCompletion = async () => {
    let { value } = this.props;
    if (!value?.id) return;
    let res = await valueActions.complete(value, null, true);
    if (res === undefined) return; // confirm dialog dismissed
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  handleConvertToValue = async () => {
    let { moment } = this.props;
    let { text, title } = this.state;
    if (!text?.length && !title?.length) {
      alert("Write something first — an empty moment can't become a value");
      return;
    }
    let conf = await valueActions.confirm(
      "confirm",
      null,
      "Convert into a child value?",
    );
    if (!conf) return;
    // flush any unsaved text before the server copies it into the new value
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    await this.updateMoment();
    let res = await momentActions.convertToValue(moment.id);
    if (!res?.success) {
      alert(res?.error || "Conversion failed");
      return;
    }
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  completeMomentAndValue = async () => {
    let { moment, value } = this.props;
    if (!moment.completed_at) {
      await momentActions.complete(moment.id, new Date());
    }
    if (!!value && !value.completed_at) {
      // prompt: false — complete the value without the confirm dialog
      await valueActions.complete(value, null, false, moment.id);
    }
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  toggleComplete = async () => {
    let { moment } = this.props;
    if (!moment?.id) return; // pseudo moments (value completion) have no id
    if (!moment.completed_at) {
      let ts = new Date(); // allow override time? maybe if moment.chapter is not today?
      let res = await momentActions.complete(moment.id, ts);
    } else {
      let res = await momentActions.uncomplete(moment.id);
    }
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  togglePinned = async () => {
    let { isPinned, moment, value } = this.props;
    let momentIds = [...value.pinned_moment_ids];
    if (!isPinned) {
      momentIds.push(moment.id);
    } else {
      momentIds = momentIds.filter((x) => x != moment.id);
    }
    let res = await valueActions.update(value.id, {
      pinned_moment_ids: momentIds,
    });
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  handleUnpinMoment = async () => {
    let { moment, value, chapter } = this.props;
    let prompt = true;
    if (!!moment.text?.length || !!moment.title?.length) {
      alert("Clear out title and text before deleting");
      return;
      // prompt = `Destroy moment? Note content will be lost!`;
    }
    let res = await momentActions.delete(moment.id, prompt);
    this.props.onRefreshChapter && this.props.onRefreshChapter();
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  styles = () => ({
    wrapper: {
      borderRadius: this.context.sizes.borderRadius,
      // backgroundColor: this.context.colors.fill,
      padding: this.context.sizes.small,
      marginRight: this.context.sizes.small,
      borderWidth: 1,
      borderColor: this.context.colors.trim,
    },
    completedWrapper: {
      borderWidth: 0,
    },
    verticalWrapper: {
      width: 42,
      height: 200,
    },
    titleText: {},
    verticalText: {
      transformOrigin: "left",
      position: "absolute",
      top: 22,
      left: 15,
      textWrap: "nowrap",
      width: 150,
      transform: [{ rotate: "90deg" }],
      // color: this.context.colors.lightForeground,
    },
    // native: rotate a wrapping View (Text transforms are unreliable there);
    // no transformOrigin, so position for center-origin rotation — a
    // 150-wide label centered in the 42×200 card
    verticalTextWrapper: {
      position: "absolute",
      top: 87,
      left: -54,
      width: 150,
      transform: [{ rotate: "90deg" }],
    },
    expandedWrapper: {
      width: ENV.WEB ? 600 : this.props.width,
    },
    progressWrapper: {
      width: 100,
    },
    todayEmptyWrapper: {
      width: 190,
    },
    headerRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      zIndex: 2,
    },
    scrollViewWrapper: {
      maxHeight: this.state.editing ? 600 : 200,
      maxWidth: ENV.WEB ? 600 : this.props.width,
    },
    input: {
      // height: 120,
      maxHeight: 400,
      // match the RichTextArea display metrics so entering/leaving edit
      // mode doesn't reflow the text
      fontSize: this.context.sizes.default,
      lineHeight: this.context.sizes.default * 1.6,
      // overflow: "hidden",
    },
    autocompleteWrapper: {
      zIndex: 99,
      position: "absolute",
      top: "100%",
      left: 0,
      // right: 0,
      // flexDirection: "row",
      // justifyContent: "flex-end",
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.trim,
        0.9,
      ),
    },
  });
}
